/**
 * Componente AnalystWorkSessionReportsWidget
 * Widget de relatórios personalizado para analistas (sem card "Valor Gerado")
 * 
 * @author Equipe GestaoeCRM
 * @date 11/10/2025
 * @version 1.0.0
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  BarChart3, 
  Clock, 
  Activity,
  Target,
  RefreshCw,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { getUserStats, UserStats } from '../services/workSessionReportsApi';

// ============================================================================
// FUNÇÕES AUXILIARES
// ============================================================================

/**
 * Formata duração em horas para formato legível
 * @param hours Horas em decimal (ex: 2.5)
 * @returns String formatada (ex: "2h 30min")
 */
const formatDuration = (hours: number): string => {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  
  if (h === 0 && m === 0) return '00:00:00';
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  
  return `${h}h ${m}min`;
};

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ComponentType<any>;
  color: 'blue' | 'green' | 'yellow' | 'purple';
}

// ============================================================================
// COMPONENTES AUXILIARES
// ============================================================================

const MetricCard: React.FC<MetricCardProps> = ({ 
  title, 
  value, 
  subtitle, 
  icon: Icon, 
  color
}) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    green: 'bg-green-50 text-green-600 border-green-100',
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
  };

  return (
    <div className={`rounded-lg border ${colorClasses[color]} p-4 transition-all duration-200 hover:shadow-md`}>
      <div className="flex items-center justify-between mb-2">
        <Icon className="h-5 w-5" />
      </div>
      
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-1">
          {title}
        </h3>
        <div className="text-2xl font-bold text-gray-900 mb-1">
          {value}
        </div>
        {subtitle && (
          <p className="text-xs text-gray-500">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

const AnalystWorkSessionReportsWidget: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<'today' | 'week' | 'month'>('today');
  
  // Query para buscar estatísticas pessoais do usuário
  const { 
    data: statsData, 
    isLoading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ['user-work-session-stats', selectedPeriod],
    queryFn: () => getUserStats(selectedPeriod),
    refetchInterval: 60000, // Atualizar a cada minuto
    staleTime: 30000, // Considerar dados frescos por 30 segundos
  });

  const stats = statsData?.data;

  // Selecionar label baseado no período
  const periodLabel = React.useMemo(() => {
    switch (selectedPeriod) {
      case 'today':
        return 'Hoje';
      case 'week':
        return 'Esta Semana';
      case 'month':
        return 'Este Mês';
      default:
        return 'Hoje';
    }
  }, [selectedPeriod]);

  // Estados de loading e erro
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          <span className="ml-2 text-sm text-gray-500">Carregando relatórios...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="text-center py-8">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <p className="text-sm text-gray-500 mb-4">
            Erro ao carregar relatórios
          </p>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="text-center py-8">
          <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-sm text-gray-500">
            Nenhum dado de relatório disponível
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <BarChart3 className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            Relatórios de Sessões
          </h3>
        </div>
        
        <div className="flex items-center space-x-2">
          {/* Seletor de período */}
          <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
            {(['today', 'week', 'month'] as const).map((period) => (
              <button
                key={period}
                onClick={() => setSelectedPeriod(period)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  selectedPeriod === period
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {period === 'today' ? 'Hoje' : period === 'week' ? 'Semana' : 'Mês'}
              </button>
            ))}
          </div>
          
          {/* Botão de atualizar */}
          <div className="flex items-center space-x-1">
            <button
              onClick={() => refetch()}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              title="Atualizar dados"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Métricas principais - SEM o card "Valor Gerado" */}
      <div className="p-6">
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {/* Sessões Ativas */}
          <MetricCard
            title="Sessões Ativas"
            value={stats.activeSessions || 0}
            subtitle="Em andamento"
            icon={Activity}
            color="green"
          />
          
          {/* Sessões Concluídas */}
          <MetricCard
            title="Concluídas"
            value={stats.completedSessions || 0}
            subtitle={periodLabel}
            icon={Target}
            color="blue"
          />
          
          {/* Total de Horas */}
          <MetricCard
            title="Horas Trabalhadas"
            value={formatDuration(stats.totalHours || 0)}
            subtitle={`${(stats.totalHours || 0).toFixed(1)}h precisas`}
            icon={Clock}
            color="purple"
          />
        </div>
      </div>
    </div>
  );
};

export default AnalystWorkSessionReportsWidget;

